import type { Decimal } from './types';

/**
 * Display helpers.
 *
 * Everything here takes the API's decimal strings and produces text. Nothing here does
 * arithmetic: a NUMERIC(38,18) does not survive a round trip through a JavaScript number, and
 * the whole point of D-07 is that the figures agree with a broker statement to the cent. If a
 * derived number is needed, the server computes it.
 */

/** Parses only for formatting. Never store or send the result back. */
function toNumber(value: Decimal | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatMoney(
  value: Decimal | null | undefined,
  currency: string,
  locale = 'en-GB',
): string | null {
  const parsed = toNumber(value);
  if (parsed === null) {
    return null;
  }
  const format = (n: number) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(n);

  // A holding of one satoshi is worth a fraction of a cent, and rounding it to "$0.00" says
  // it is worth nothing - which is the one thing it is not. FR-C3 exists to keep that figure
  // exact through the database and the wire; throwing it away in the formatter would undo
  // the whole chain at the last step.
  if (parsed !== 0 && Number(parsed.toFixed(2)) === 0) {
    return parsed < 0 ? `> -${format(0.01)}` : `< ${format(0.01)}`;
  }
  return format(parsed);
}

export function formatPercent(
  value: Decimal | null | undefined,
  locale = 'en-GB',
  digits = 2,
): string | null {
  const parsed = toNumber(value);
  if (parsed === null) {
    return null;
  }
  const format = (n: number) =>
    `${new Intl.NumberFormat(locale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(n)}%`;

  // Same rule as money: a real weight that rounds to 0.00% is not zero.
  if (parsed !== 0 && Number(parsed.toFixed(digits)) === 0) {
    const smallest = Number(`1e-${digits}`);
    return parsed < 0 ? `> -${format(smallest)}` : `< ${format(smallest)}`;
  }
  return format(parsed);
}

/**
 * Quantities keep their significant decimals. A crypto balance is not two decimal places, and
 * rounding 0.00000001 BTC to 0.00 on screen would contradict FR-C3 in the one place the user
 * actually looks.
 */
export function formatQuantity(value: Decimal | null | undefined, locale = 'en-GB'): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  // Defensive. Decimals cross the wire as strings (the backend serialises BigDecimal that way
  // so precision survives), but a field that slipped through as a number must format rather
  // than throw - a whole page blanking over one cell is the wrong failure.
  const text = typeof value === 'string' ? value : String(value);
  const trimmed = text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text;
  const parsed = toNumber(trimmed);
  if (parsed === null) {
    return trimmed;
  }
  const decimals = trimmed.includes('.') ? (trimmed.split('.')[1]?.length ?? 0) : 0;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.min(decimals, 8),
  }).format(parsed);
}

export function formatDate(iso: string | null | undefined, locale = 'en-GB'): string | null {
  if (!iso) {
    return null;
  }
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso));
}

/**
 * The ticker inside a symbol: `AVGO  270219C00360000` reads as `AVGO`, `AVGO` stays `AVGO`.
 *
 * A person holds AVGO, not `AVGO  270219C00360000`. The OCC symbol is how the position is
 * stored and reconciled, and it belongs in a contract line, a tooltip or an export - not in a
 * column someone scans down. Where two rows would collapse to the same ticker (the stock and an
 * option on it), the caller shows the contract underneath rather than putting the digits back
 * in the heading.
 *
 * Deliberately strict: the canonical OCC layout is a root padded to 6 characters, then YYMMDD,
 * then C or P, then a strike in eight digits - exactly 21. Anything that is not that shape is
 * returned untouched, because a symbol this cannot parse is more likely an ordinary ticker than
 * a contract worth guessing at, and a wrong ticker on a holdings row is a wrong position.
 */
export function tickerOfSymbol(symbol: string | null | undefined): string {
  if (!symbol) {
    return '';
  }
  const occ = /^([A-Z0-9.]{1,6}) *(\d{6})([CP])(\d{8})$/.exec(symbol);
  return occ?.[1] ?? symbol;
}

/** Positive, negative or neither - for colouring a figure without re-parsing it everywhere. */
export function signOf(value: Decimal | null | undefined): 'up' | 'down' | 'flat' | null {
  const parsed = toNumber(value);
  if (parsed === null) {
    return null;
  }
  return parsed > 0 ? 'up' : parsed < 0 ? 'down' : 'flat';
}

export function isoDaysAgo(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

export function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * The difference between two base-currency totals, as a decimal string, for display.
 *
 * This is the one place the client subtracts money, and it is deliberately narrow. It is
 * safe here because both operands are totals in one currency at cent scale, where a
 * float64 carries about fifteen significant digits - far more than any portfolio total
 * needs - and the result is immediately rounded to two decimals for a label.
 *
 * It is NOT safe on quantities: a crypto balance carries eighteen decimals and would lose
 * precision here, which is the whole reason FR-C3 exists. Do not widen this function.
 *
 * The right home for a day-over-day delta is the server, beside the snapshots it compares.
 * Until that endpoint exists this computes it for one tile.
 */
export function differenceForDisplay(
  current: Decimal | null | undefined,
  previous: Decimal | null | undefined,
): Decimal | null {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return null;
  }
  const a = Number(current);
  const b = Number(previous);
  if (!Number.isFinite(a) || !Number.isFinite(b)) {
    return null;
  }
  return (a - b).toFixed(2);
}

/** The same difference as a percentage of the earlier figure. Null when it was zero. */
export function percentChangeForDisplay(
  current: Decimal | null | undefined,
  previous: Decimal | null | undefined,
): Decimal | null {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return null;
  }
  const a = Number(current);
  const b = Number(previous);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b === 0) {
    return null;
  }
  return (((a - b) / Math.abs(b)) * 100).toFixed(2);
}
